<template>
  <modal-component
    :is-displayed="isEditBulkModalOpened"
    :no-padding="true"
    :spots="spotsToEdit"
    :spaceId="parkingSpaceId"
    title="Edit multiple parking spots"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <input-field
              v-model="parkingSpot.name"
              :label="$t('Label')"
              :errorMessage="v$?.parkingSpot['name']?.$errors[0]?.$message"
              :isError="v$?.parkingSpot['name']?.$error"
              :required="true"
            />
            <input-field
              v-model="parkingSpot.startNumber"
              label="Start Number"
              :errorMessage="v$?.parkingSpot['startNumber']?.$errors[0]?.$message"
              :isError="v$?.parkingSpot['startNumber']?.$error"
              :required="true"
            />
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("ParkingSpots.Type")
            }}</label>
            <div class="flex items-center justify-center">
              <small-loader v-if="isParkingSpotListLoading" height="h-8" width="w-8" />
              <v-select
                v-else
                v-model="parkingSpot.typeId"
                :class="`mt-1 w-full`"
                :options="parkingSpotTypes"
                :reduce="(space: any) => space.id"
                label="name"
              />
            </div>
          </div>
          <checkbox-input
            v-model="parkingSpot.public"
            :is-checked="parkingSpot.public"
            :label="$t('ParkingSpots.Public')"
          />
          <checkbox-input
            v-model="parkingSpot.integration"
            :is-checked="parkingSpot.integration"
            :label="$t('ParkingSpots.Integration')"
          />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import TheButton from "@/core/Components/TheButton.vue";
import ParkingSpaceService from "../Services/ParkingSpaceService";
import useValidate from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import { ParkingSpace } from "../Services/ParkingSpaceTypes";
import { ParkingSpot, ParkingSpotType, UpdateBulkParkingSpot } from "../../ParkingSpots/Services/ParkingSpotTypes";
import { ErrorMessageFormatter, isString } from "@/core/Services/GlobalFunctions";
import ParkingSpotService from "../../ParkingSpots/Services/ParkingSpotService";
import { useToast } from "vue-toastification";
import { lettersAndNumbersValidator, numbersValidator } from "@/core/Services/CustomValidators";

export default defineComponent({
  components: {
    ButtonWrapper,
    InputField,
    FormDivider,
    ModalComponent,
    FormWrapper,
    VSelect,
    SmallLoader,
    TheButton,
    CheckboxInput,
  },
  props: {
    isEditBulkModalOpened: Boolean,
    spotsToEdit: {
      type: Array as PropType<UpdateBulkParkingSpot[]>,
      required: true,
    },
    parkingSpaceId: {
      type: Number,
      required: true,
    },
  },
  emits: ["toggle-modal", "bulk-edit-data", "refresh"],
  data() {
    return {
      parkingSpotService: new ParkingSpotService(),
      toast: useToast(),
      isEditBulkModalOpenedss: false,
      isParkingSpotListLoading: false,
      isSubmitLoading: false,
      isLoading: false,
      v$: useValidate() as any,
      validationErrors: [] as any,
      parkingSpaceService: new ParkingSpaceService(),
      parkingSpaces: [] as ParkingSpace[],
      parkingSpots: [] as ParkingSpot[],
      parkingSpot: {
        name: "",
        startNumber: 0,
        public: false,
        typeId: null,
        integration: false,
      } as UpdateBulkParkingSpot,
      parkingSpotTypes: [] as ParkingSpotType[],
    };
  },
  validations() {
    return {
      parkingSpot: {
        name: {
          required,
          maxLength: maxLength(100),
          lettersAndNumbers: lettersAndNumbersValidator,
        },
        startNumber: {
          required,
          numbers: numbersValidator,
        },
        typeId: {
          required,
        },
      },
    };
  },
  watch: {
    spotsToEdit: {
      handler(newSpots: UpdateBulkParkingSpot[]) {
        if (newSpots.length === 0) {
          this.parkingSpot.name = "";
          this.parkingSpot.typeId = null;
          this.parkingSpot.startNumber = 1;
          this.parkingSpot.integration = false;
          this.parkingSpot.public = false;
          return;
        }

        const firstTypeId = newSpots[0].typeId;
        const firstName = newSpots[0].name || "";
        const firstPublic = newSpots[0].public;
        const firstIntegration = newSpots[0].integration;

        const allSameType = newSpots.every((spot: UpdateBulkParkingSpot) => spot.typeId === firstTypeId);
        const allSameName = newSpots.every(
          (spot: UpdateBulkParkingSpot) => spot.name?.split("-")[0] === firstName.split("-")[0]
        );
        const allSamePublic = newSpots.every((spot: UpdateBulkParkingSpot) => spot.public === firstPublic);
        const allSameIntegration = newSpots.every(
          (spot: UpdateBulkParkingSpot) => spot.integration === firstIntegration
        );

        const startNumbers = newSpots.map((spot: UpdateBulkParkingSpot) => Number(spot.name?.split("-")[1]));
        const minStartNumber = Math.min(...startNumbers);
        const allSameSquence = startNumbers.every((number, index) => number === minStartNumber + index);

        this.parkingSpot.name = allSameName ? firstName.split("-")[0] : "";
        this.parkingSpot.typeId = allSameType ? firstTypeId : null;
        this.parkingSpot.startNumber = allSameSquence ? minStartNumber : 1;
        this.parkingSpot.public = allSamePublic ? firstPublic : false;
        this.parkingSpot.integration = allSameIntegration ? firstIntegration : false;
      },
      immediate: true,
      deep: true,
    },
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getParkingSpotTypes();
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error(ErrorMessageFormatter(error));
        return;
      }
      this.toast.error(error.response.data);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    handleSubmit() {
      this.v$.$validate();
      this.validationErrors = this.v$.$errors;
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          this.toast.success((this as any).$t("ParkingSpots.EditBulkSavedSuccess"));
          this.$emit("bulk-edit-data", this.parkingSpot);
          this.$emit("refresh");
          this.$emit("toggle-modal");
        } catch (error: any) {
          if (!isString(error.response.data)) {
            this.toast.error(ErrorMessageFormatter(error));
            return;
          }
          this.toast.error(error.response.data);
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },

    async getParkingSpotTypes() {
      this.isSubmitLoading = true;
      try {
        this.parkingSpotTypes = await this.parkingSpotService.getParkingSpotTypes();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },
  },
});
</script>
